import { useCallback } from 'react'

import { Button } from '@/components/button.tsx'
import {
  faBackwardStep,
  faForwardStep,
  faPause,
  faPlay,
  faStop,
} from '@fortawesome/free-solid-svg-icons'

import { useHotkeys } from '@tanstack/react-hotkeys'
import { useAtomValue } from 'jotai'

import { RundownPermission } from '~shared/rundown/permission'
import { RunnerState } from '~shared/types'

import {
  isRunningAtom,
  runnerNextCueIdAtom,
  runnerStateAtom,
} from '@/store/runner.store.js'

import eventBus from '../../../utils/eventBus.js'
import { useRundownPermission } from '../../permissions/lib/use-rundown-permission.ts'
import { useIsFirstCue } from '../cue/cue.hooks.ts'

import { TRANSPORT_HOTKEYS } from './transport.hotkeys.ts'
import { isRunnerLoadingAtom, useTransportActions } from './transport.ts'
import { TransportCountdown } from './TransportCountdown.tsx'
import { TransportProgressBar } from './TransportProgressBar.tsx'
import { TransportStateIndicator } from './TransportStateIndicator.tsx'
import { TransportTimeOperation } from './TransportTimeOperation.tsx'
import { TransportTooltip } from './TransportTooltip.tsx'
import { useStartShowHotkey } from './use-start-show-hotkey.ts'

export function TransportControls() {
  const runnerState = useAtomValue(runnerStateAtom)
  const isRunning = useAtomValue(isRunningAtom)
  const isRunnerLoading = useAtomValue(isRunnerLoadingAtom)

  const writeAccess = useRundownPermission({
    permission: RundownPermission.EDIT,
  })

  const readonly = !writeAccess

  const isShowCaller = useRundownPermission({
    permission: RundownPermission.SHOWCALL,
  })

  const nextCueId = useAtomValue(runnerNextCueIdAtom)
  const isFirstCue = useIsFirstCue()
  const isLastCue = !nextCueId

  const {
    onPlay,
    onPause,
    onPrev,
    onNext,
    onCreateRunner: onStartShow,
    onUnsetRunner: onResetShow,
    onEndRunner: onEndShow,
    onTimeOperation,
  } = useTransportActions()

  const canShowCall = !readonly && !isRunnerLoading && isShowCaller

  // Space advances the show, it never starts it. Starting is Alt+Space so a
  // stray spacebar ahead of the show can't put the rundown on air.
  const handleNext = useCallback(() => {
    if (!canShowCall) return
    if (runnerState !== RunnerState.ONAIR) return
    if (nextCueId) void onNext()
    else void onEndShow()
  }, [canShowCall, nextCueId, onEndShow, onNext, runnerState])

  const handleStartShow = useCallback(() => {
    if (!canShowCall) return
    if (runnerState !== RunnerState.PRESHOW) return
    void onStartShow()
  }, [canShowCall, onStartShow, runnerState])

  useStartShowHotkey(handleStartShow)

  // Handle Keyboard shortcuts
  useHotkeys(
    [
      { hotkey: TRANSPORT_HOTKEYS.next, callback: handleNext },
      {
        hotkey: 'Mod+Z',
        callback: () => writeAccess && eventBus.$emit('openTrashModal'),
      },
    ],
    {
      // Override default behaviors
      ignoreInputs: true,
      requireReset: true,
    },
  )

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="flex-none flex gap-1 h-9">
        {/* Current Cue */}

        <div className="h-9 flex items-center gap-3 pr-3 leading-0">
          <div className="w-4 text-center">
            <TransportStateIndicator />
          </div>

          <TransportCountdown />
        </div>

        {/* Subtract Time */}
        <TransportTimeOperation
          operation="subtract"
          readonly={readonly}
          loading={isRunnerLoading}
          runnerState={runnerState}
          isShowCaller={isShowCaller}
          onTimeOperation={onTimeOperation}
        />

        <Button
          onClick={onPrev}
          toolTip="Back to the previous cue"
          icon={faBackwardStep}
          colour="dark"
          buttonClass="w-full"
          loading={isRunnerLoading}
          disabled={
            readonly ||
            runnerState !== RunnerState.ONAIR ||
            isRunnerLoading ||
            isFirstCue ||
            !isShowCaller
          }
        />

        {/* Pause / Play */}
        <Button
          onClick={isRunning ? onPause : onPlay}
          colour="dark"
          wrapperClass="flex-none"
          buttonClass="w-24!"
          loading={isRunnerLoading}
          disabled={
            readonly ||
            isRunnerLoading ||
            runnerState !== RunnerState.ONAIR ||
            !isShowCaller
          }
          icon={isRunning ? faPause : faPlay}
          toolTip={isRunning ? 'Pause' : 'Resume'}
        />

        {/* Start / Next / Reset */}
        {(runnerState === RunnerState.PRESHOW && (
          <Button
            onClick={onStartShow}
            text="Start show"
            toolTip={
              <TransportTooltip
                label="Start the show, will start timer on selected or first cue"
                shortcut={TRANSPORT_HOTKEYS.startShow}
              />
            }
            colour="red"
            wrapperClass="grow"
            buttonClass="w-full"
            loading={isRunnerLoading}
            disabled={readonly || isRunnerLoading || !isShowCaller}
          />
        )) ||
          (runnerState === RunnerState.ONAIR && (
            <Button
              onClick={isLastCue ? onEndShow : onNext}
              text={isLastCue ? 'End show' : 'Next cue'}
              toolTip={
                <TransportTooltip
                  label={
                    isLastCue
                      ? 'End the show, stop timer and keep elapsed times on screen'
                      : 'Advance show to the next selected cue'
                  }
                  shortcut={TRANSPORT_HOTKEYS.next}
                />
              }
              icon={isLastCue ? faStop : faForwardStep}
              colour="dark"
              wrapperClass="grow"
              buttonClass="w-full"
              loading={isRunnerLoading}
              disabled={readonly || isRunnerLoading || !isShowCaller}
            />
          )) ||
          (runnerState === RunnerState.ENDED && (
            <Button
              onClick={onResetShow}
              text="Reset show"
              toolTip="Reset the show timing back to the planning phase"
              icon={faBackwardStep}
              colour="dark"
              wrapperClass="grow"
              buttonClass="w-full"
              loading={isRunnerLoading}
              disabled={readonly || isRunnerLoading || !isShowCaller}
            />
          ))}

        {/* End show, always reachable while on air. On the last cue the
            primary button already ends the show, so it would be a duplicate. */}
        {!isLastCue && (
          <Button
            onClick={onEndShow}
            icon={faStop}
            colour="dark"
            wrapperClass="flex-none"
            loading={isRunnerLoading}
            disabled={
              readonly ||
              isRunnerLoading ||
              runnerState !== RunnerState.ONAIR ||
              !isShowCaller
            }
            toolTip="End the show, stop timer and keep elapsed times on screen"
          />
        )}

        {/* Add Time */}
        <TransportTimeOperation
          operation="add"
          readonly={readonly}
          loading={isRunnerLoading}
          runnerState={runnerState}
          isShowCaller={isShowCaller}
          onTimeOperation={onTimeOperation}
        />
      </div>

      <TransportProgressBar />
    </div>
  )
}
